/**
 * Rate cards (TMMPDM-0086; one list since TMMPDM-0089): a responsive grid of cards. Each card shows
 * its name, currency and version, its status, a few prices (retail and wholesale) and its resellers,
 * and is one link to its page. No cost, profit or AI term is on a card. The grid ends with a dotted
 * Add new rate card tile. Filters (`?status=`, Active by default) and the name search (`?q=`) are in
 * the URL; `?create=1` (the palette's New rate card) opens the create dialog.
 * TMMPDM-0090 (tmm-api 0155): the create dialog takes a name, a currency and, optionally, the card's
 * first controls; every price is set on Prices.
 */
import { useEffect, useState, type ReactNode } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { adminApi } from '@/api/tmm/admin';
import { ApiError, errorCopy, isValidationFailed, validationFields } from '@/api/errors';
import type {
  CustomerCardControlsCreate,
  CustomerRateCardVersionView,
  CustomerRateCardView,
} from '@/api/tmm/types';
import { inputValue, toWire, useFieldCheck } from '@/components/settings/settingFields';
import { EmptyState, ErrorState, PageHeader, SubmitButton } from '@/components/page';
import { Amount, AppSwitch, FilterTabs } from '@/components/ui';
import { useCopy } from '@/hooks/useCopy';
import { oneOf, useUrlState, type UrlSchema } from '@/hooks/useUrlState';
import { foldForMatch } from '@/components/palette/usePaletteSources';
import { StatusChip } from '@/status';
import { useResellerNames } from '@/routes/resellers/useResellerNames';
import {
  currentVersion,
  displayVersion,
  simpleBands,
  versionChipLabel,
} from '@/routes/customerCards/customerRateCardModel';
import { useCardControlFields } from './cardControlFields';
import { CURRENCIES } from './rateCardInputs';
import { RateCardStatusChip } from './rateCardChips';

const FILTERS = ['active', 'drafts', 'all'] as const;

type ListUrl = { status: (typeof FILTERS)[number]; q: string; create: '1' | undefined };

const SCHEMA: UrlSchema<ListUrl> = {
  status: { default: 'active', parse: oneOf(FILTERS) },
  q: { default: '', parse: (raw) => raw },
  create: { default: undefined, parse: oneOf(['1'] as const) },
};

function shownUnder(card: CustomerRateCardView, filter: ListUrl['status']): boolean {
  if (filter === 'active') return card.status === 'PUBLISHED';
  if (filter === 'drafts') return card.status === 'DRAFT';
  return true;
}

export function RateCardsPage() {
  const { t } = useCopy();
  const navigate = useNavigate();
  const [url, setUrl] = useUrlState(SCHEMA);
  const [cards, setCards] = useState<CustomerRateCardView[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [createOpen, setCreateOpen] = useState(false);
  const names = useResellerNames();

  useEffect(() => {
    let cancelled = false;
    adminApi
      .listCustomerRateCards()
      .then((list) => {
        if (cancelled) return;
        setCards(list.rateCards);
        setError(null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setCards(null);
        setError(err);
      });
    return () => {
      cancelled = true;
    };
  }, [reloadToken]);

  const dialogOpen = createOpen || url.create === '1';
  const closeCreate = () => {
    setCreateOpen(false);
    if (url.create === '1') setUrl({ create: undefined }, { history: 'replace' });
  };

  const term = foldForMatch(url.q.trim());
  const visible =
    cards?.filter(
      (card) =>
        shownUnder(card, url.status) && (term === '' || foldForMatch(card.name).includes(term)),
    ) ?? [];

  return (
    <>
      <PageHeader
        title={t('page.rateCards.title', 'Rate cards')}
        subtitle={t(
          'rateCard.list.subtitle',
          'Each card holds our cost, wholesale and retail. Grant it to resellers; each reseller adopts one.',
        )}
      />
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        sx={{ mb: 2, alignItems: { sm: 'center' }, justifyContent: 'space-between' }}
      >
        <FilterTabs
          ariaLabel={t('rateCard.list.filterAria', 'Rate card status')}
          active={url.status}
          onChange={(key) => {
            if ((FILTERS as readonly string[]).includes(key)) {
              setUrl({ status: key as ListUrl['status'] });
            }
          }}
          tabs={[
            { key: 'active', label: t('rateCard.list.filter.active', 'Active') },
            { key: 'drafts', label: t('rateCard.list.filter.drafts', 'Drafts') },
            { key: 'all', label: t('rateCard.list.filter.all', 'All') },
          ]}
        />
        <TextField
          size="small"
          type="search"
          label={t('rateCard.list.search', 'Search by name')}
          value={url.q}
          onChange={(event) => setUrl({ q: event.target.value }, { history: 'replace' })}
        />
      </Stack>
      {error ? (
        <ErrorState
          error={error}
          message={t('rateCard.list.loadError', 'Could not load rate cards.')}
          onRetry={() => setReloadToken((n) => n + 1)}
        />
      ) : cards === null ? (
        <Alert severity="info">{t('common.loading', 'Loading…')}</Alert>
      ) : (
        <>
          {cards.length > 0 && visible.length === 0 ? (
            <Box sx={{ mb: 2 }}>
              <EmptyState
                surface
                title={t(
                  'rateCard.list.filteredEmpty',
                  'No rate cards match. Try All, or another name.',
                )}
              />
            </Box>
          ) : null}
          <Box
            component="ul"
            aria-label={t('nav.rateCards', 'Rate cards')}
            sx={{
              listStyle: 'none',
              m: 0,
              p: 0,
              display: 'grid',
              gap: 2,
              gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
            }}
          >
            {visible.map((card) => (
              <Box component="li" key={card.id} sx={{ display: 'flex' }}>
                <RateCardTile card={card} names={names.names} />
              </Box>
            ))}
            <Box component="li" sx={{ display: 'flex' }}>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => setCreateOpen(true)}
                sx={{ flex: 1, minHeight: 160, borderStyle: 'dashed', borderWidth: 2 }}
              >
                {t('rateCard.list.add', 'Add new rate card')}
              </Button>
            </Box>
          </Box>
        </>
      )}
      <CreateRateCardDialog
        open={dialogOpen}
        onClose={closeCreate}
        onCreated={(id) => {
          setCreateOpen(false);
          // The new card opens on Prices, with its first draft.
          const search = new URLSearchParams({ tab: 'prices' }).toString();
          void navigate({ pathname: `/rate-cards/${id}`, search }, { replace: url.create === '1' });
        }}
      />
    </>
  );
}

function PriceRow({
  label,
  retail,
  wholesale,
  currency,
}: {
  label: ReactNode;
  retail: number;
  wholesale: number;
  currency: string;
}) {
  return (
    <>
      <Typography component="span" variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography component="span" variant="body2" sx={{ textAlign: 'right' }}>
        <Amount minor={retail} currency={currency} />
      </Typography>
      <Typography component="span" variant="body2" sx={{ textAlign: 'right' }}>
        <Amount minor={wholesale} currency={currency} />
      </Typography>
    </>
  );
}

function PriceGrid({
  version,
  currency,
}: {
  version: CustomerRateCardVersionView;
  currency: string;
}) {
  const { t } = useCopy();
  const guests = version.seatBands.length > 0 ? simpleBands(version.seatBands) : null;
  const connections =
    version.connectedAccountBands.length > 0 ? simpleBands(version.connectedAccountBands) : null;
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) auto auto',
        columnGap: 1.5,
        rowGap: 0.25,
        my: 1,
      }}
    >
      <span />
      <Typography
        component="span"
        variant="caption"
        color="text.secondary"
        sx={{ textAlign: 'right' }}
      >
        {t('rateCard.prices.retail', 'Retail')}
      </Typography>
      <Typography
        component="span"
        variant="caption"
        color="text.secondary"
        sx={{ textAlign: 'right' }}
      >
        {t('rateCard.prices.wholesale', 'Wholesale')}
      </Typography>
      <PriceRow
        label={t('rateCard.grid.monthly', 'Monthly')}
        retail={version.baseMinor}
        wholesale={version.baseWholesaleMinor}
        currency={currency}
      />
      {version.annualBaseMinor !== null ? (
        <PriceRow
          label={t('rateCard.grid.annual', 'Annual')}
          retail={version.annualBaseMinor}
          wholesale={version.annualBaseWholesaleMinor}
          currency={currency}
        />
      ) : null}
      {guests ? (
        <PriceRow
          label={t('rateCard.grid.perGuestOver', 'Per guest over {{n, number}}', {
            n: guests.included,
          })}
          retail={guests.perUnit.retailMinor}
          wholesale={guests.perUnit.wholesaleMinor}
          currency={currency}
        />
      ) : null}
      {connections ? (
        <PriceRow
          label={t('rateCard.grid.perConnectionOver', 'Per connection over {{n, number}}', {
            n: connections.included,
          })}
          retail={connections.perUnit.retailMinor}
          wholesale={connections.perUnit.wholesaleMinor}
          currency={currency}
        />
      ) : null}
    </Box>
  );
}

/** One card in the grid: one link, named for the card, its currency and version. */
function RateCardTile({ card, names }: { card: CustomerRateCardView; names: Map<number, string> }) {
  const { t } = useCopy();
  const current = currentVersion(card);
  const version = displayVersion(card);
  const versionText = current
    ? versionChipLabel(current.versionNo, t)
    : t('rateCard.draftOnly', 'draft');
  const named = card.grants.filter((id) => names.has(id));
  return (
    <Card variant="outlined" sx={{ flex: 1, display: 'flex' }}>
      <CardActionArea
        component={RouterLink}
        to={`/rate-cards/${card.id}`}
        aria-label={t('rateCard.list.cardLink', '{{name}}, {{currency}} · {{versionLabel}}', {
          name: card.name,
          currency: card.currency,
          versionLabel: versionText,
        })}
        sx={{
          alignItems: 'stretch',
          '&:focus-visible': {
            outline: '2px solid',
            outlineColor: 'primary.main',
            outlineOffset: 2,
          },
        }}
      >
        <CardContent>
          <Stack
            direction="row"
            spacing={1}
            sx={{ justifyContent: 'space-between', alignItems: 'flex-start' }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 600 }}>
                {card.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {`${card.currency} · ${versionText}`}
              </Typography>
            </Box>
            <RateCardStatusChip status={card.status} />
          </Stack>
          {version ? <PriceGrid version={version} currency={card.currency} /> : null}
          <Typography variant="body2" sx={{ mt: 1 }}>
            {card.grants.length === 0
              ? t('rateCard.list.notGranted', 'Not granted yet')
              : t(
                  'rateCard.list.resellers',
                  '{{granted, number}} granted, {{adopted, number}} adopted',
                  {
                    granted: card.grants.length,
                    adopted: card.adoptingResellers.length,
                  },
                )}
          </Typography>
          {named.length > 0 ? (
            <Stack direction="row" useFlexGap spacing={0.5} sx={{ flexWrap: 'wrap', mt: 0.5 }}>
              {named.map((id) => {
                const name = names.get(id) ?? '';
                // Adopted is success-tinted and says so in words (never colour alone); granted only is neutral.
                return card.adoptingResellers.includes(id) ? (
                  <StatusChip
                    key={id}
                    tone="success"
                    icon={<CheckIcon />}
                    label={t('rateCard.list.pillAdopted', '{{name}} (adopted)', { name })}
                  />
                ) : (
                  <StatusChip
                    key={id}
                    tone="neutral"
                    label={t('rateCard.list.pillGranted', '{{name}}', { name })}
                  />
                );
              })}
            </Stack>
          ) : null}
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

/**
 * The first controls row's starting values: the defaults `CustomerCardControlsCreate` states
 * (tmm-api `b1f55fab`, unchanged since `092b43c5`), in wire units. They only prefill the inputs; a
 * field left empty is omitted and the server's default applies.
 */
const CONTROL_DEFAULTS: Readonly<Record<keyof CustomerCardControlsCreate, number>> = {
  maxAiSpendMinor: 5000,
  aiDailyCap: 50,
  aiMaxInflight: 3,
  aiMaxAutoRecharges30d: 5,
  graceDaysAfterEnd: 0,
  affiliateRecurringMaxBp: 5000,
  affiliateBountyMaxMinor: 10000,
  affiliateMilestoneMaxMinor: 50000,
  affiliateCoolOffMaxDays: 120,
};

const CONTROLS_PREFIX = 'controls.';

/**
 * Add new rate card (TMMPDM-0090, tmm-api 0155): a name and a currency, and optionally the card's
 * first controls. The card is created at a base of 0 and every price — our cost, wholesale and
 * retail — is set on Prices, where the dialog lands: since 0155 a retail price can't be created
 * without its wholesale. With "Set the controls now" off nothing about controls is sent; on, each
 * filled field is sent and an emptied one is left out, so the server's default applies. There is no
 * tax on create (MM-D-137), nor any card tax since tmm-api 0156 (MM-D-139).
 */
function CreateRateCardDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (id: number) => void;
}) {
  const { t } = useCopy();
  const controlFields = useCardControlFields();
  const check = useFieldCheck();
  const [name, setName] = useState('');
  const [currency, setCurrency] = useState<string>(CURRENCIES[0]);
  const [setControls, setSetControls] = useState(false);
  const [controls, setControlValues] = useState<Record<string, string>>({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const close = () => {
    if (busy) return;
    setName('');
    setCurrency(CURRENCIES[0]);
    setSetControls(false);
    setControlValues({});
    setFieldErrors({});
    setError(null);
    onClose();
  };

  const toggleControls = (on: boolean) => {
    setSetControls(on);
    setFieldErrors({});
    setControlValues(
      on
        ? Object.fromEntries(
            controlFields.map((field) => [
              field.key,
              inputValue(
                field,
                CONTROL_DEFAULTS[field.key as keyof CustomerCardControlsCreate],
                currency,
              ),
            ]),
          )
        : {},
    );
  };

  /** The filled fields only; `undefined` when nothing is to be sent. */
  const controlsBody = (): CustomerCardControlsCreate | undefined => {
    if (!setControls) return undefined;
    const body: Record<string, unknown> = {};
    for (const field of controlFields) {
      const raw = controls[field.key] ?? '';
      if (raw.trim() !== '') body[field.key] = toWire(field, raw, currency);
    }
    return Object.keys(body).length > 0 ? (body as CustomerCardControlsCreate) : undefined;
  };

  const submit = async () => {
    const trimmed = name.trim();
    const next: Record<string, string> = {};
    if (setControls) {
      for (const field of controlFields) {
        const raw = controls[field.key] ?? '';
        const message = raw.trim() === '' ? null : check(field, raw, currency);
        if (message) next[field.key] = message;
      }
    }
    setFieldErrors(next);
    if (trimmed === '') {
      setError(t('customerRateCards.create.nameRequired', 'Name is required.'));
      return;
    }
    if (Object.keys(next).length > 0) {
      setError(null);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const first = controlsBody();
      let card = await adminApi.createCustomerRateCard({
        name: trimmed,
        currency,
        baseMinor: 0,
        ...(first ? { controls: first } : {}),
      });
      if (card.versions.length === 0) {
        await adminApi.addCustomerRateCardVersion(card.id, { baseMinor: 0 });
        card = await adminApi.getCustomerRateCard(card.id);
      }
      onCreated(card.id);
    } catch (err) {
      if (err instanceof ApiError && String(err.code) === 'RATE_CARD_NAME_TAKEN') {
        setError(t('rateCard.create.nameClash', 'Another rate card has this name.'));
      } else {
        // A refused control lands on its input; anything else is said in the dialog.
        const placed: Record<string, string> = {};
        let unplaced = !isValidationFailed(err);
        if (isValidationFailed(err)) {
          for (const [field, message] of Object.entries(validationFields(err, t))) {
            const key = field.startsWith(CONTROLS_PREFIX)
              ? field.slice(CONTROLS_PREFIX.length)
              : '';
            if (setControls && controlFields.some((control) => control.key === key)) {
              placed[key] = message;
            } else {
              unplaced = true;
            }
          }
          if (Object.keys(placed).length === 0) unplaced = true;
        }
        setFieldErrors(placed);
        setError(
          unplaced
            ? errorCopy(err, t, t('rateCard.create.error', "Couldn't create the rate card."))
            : null,
        );
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={close}
      fullWidth
      maxWidth="sm"
      aria-labelledby="new-rate-card-title"
    >
      <DialogTitle id="new-rate-card-title">
        {t('rateCard.list.add', 'Add new rate card')}
      </DialogTitle>
      <DialogContent>
        <TextField
          required
          fullWidth
          margin="normal"
          label={t('rateCards.form.name', 'Name')}
          value={name}
          onChange={(event) => setName(event.target.value)}
          slotProps={{ htmlInput: { maxLength: 80 } }}
        />
        <TextField
          select
          required
          fullWidth
          margin="normal"
          label={t('rateCards.form.currency', 'Currency')}
          value={currency}
          onChange={(event) => setCurrency(event.target.value)}
        >
          {CURRENCIES.map((code) => (
            <MenuItem key={code} value={code}>
              {code}
            </MenuItem>
          ))}
        </TextField>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {t(
            'rateCard.create.hint',
            'The first draft opens on Prices, where you set our cost, wholesale and retail.',
          )}
        </Typography>
        <AppSwitch
          label={t('rateCard.create.setControls', 'Set the controls now')}
          checked={setControls}
          onChange={toggleControls}
        />
        <Typography variant="body2" color="text.secondary">
          {setControls
            ? t(
                'rateCard.create.controlsOn',
                'Prefilled with the standard values. A field left empty takes the standard value.',
              )
            : t(
                'rateCard.create.controlsOff',
                'The card starts with the standard controls. You can schedule a change on its Controls tab.',
              )}
        </Typography>
        {setControls
          ? controlFields.map((field) => (
              <TextField
                key={field.key}
                id={`new-rate-card-${field.key}`}
                fullWidth
                margin="normal"
                label={field.label}
                value={controls[field.key] ?? ''}
                onChange={(event) => {
                  const value = event.target.value;
                  setControlValues((prev) => ({ ...prev, [field.key]: value }));
                  setFieldErrors(({ [field.key]: _dropped, ...others }) => others);
                }}
                error={Boolean(fieldErrors[field.key])}
                helperText={fieldErrors[field.key] ?? field.help}
                slotProps={{
                  htmlInput: { inputMode: field.kind === 'integer' ? 'numeric' : 'decimal' },
                }}
              />
            ))
          : null}
        {error ? (
          <Alert severity="error" sx={{ mt: 1 }}>
            {error}
          </Alert>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={busy}>
          {t('common.cancel', 'Cancel')}
        </Button>
        <SubmitButton busy={busy} onClick={() => void submit()}>
          {t('customerRateCards.create.submit', 'Create card')}
        </SubmitButton>
      </DialogActions>
    </Dialog>
  );
}
